import * as React from "react";
import SelectInput, { InputSelectProps } from "./SelectInput";
import SelectOptions from "./SelectOptions";
import { ChildrenRender, Fields, SelectionType } from "./select.typings";
import useOnclickOutside from "../../hooks/useOnclickOutside";
import { mapToAnArrayOfItems, mapToNormalizeSelectedItems, mapToSingleItem } from "./select.mapper";
import { useSelect } from "./useSelect";


type BaseSelectProps<T> = ChildrenRender<T> &
  InputSelectProps & {
    id?: string;
    items: T[];
    singleSelect: boolean;
    selectedItems: SelectionType<T>;
    readOnlyItems?: T[];
    idField: Fields<T>;
    labelField: Fields<T>;
    isSuggestionField?: Fields<T>;
    onClearSuggestions?: (items: T[]) => void;
    onChange?: (selected: SelectionType<T>) => void;
    onTermChange?: (searchTerm: string) => unknown;
    isFilterMode: boolean;
    isLoading?: boolean;
    keepOrder?: boolean;
    errorMessage?: string;
    dropdownZIndex?: number;
    ignoreClickOutside?: boolean;
    isCanCreateTag?: boolean;
    showCreateAction?: boolean;
    createActionText?: string;
    eraseSearchTermOnClick?: boolean;
    top?: boolean;
    autofocus?: boolean;
    noResultMessage?: string;
    onClear?: () => void;
    itemsRenderer?: (items: T[], searchTerm?: string, onSelect?: (element: T) => void) => React.ReactElement;
    onRemoveSuggestion?: (item: T) => void;
    onCreatClick?: (text: string) => void;
    onCreateTag?: (text: string) => void;
    onClose?: () => void;
    quickSearchPredicate?: (element: T) => boolean;
  };

export type SelectProps<T> = Omit<
  BaseSelectProps<T>,
  "onChange" | "selectedItems" | "singleSelect" | "isFilterMode" | "isLoading"
> & {
  selectedItems: T[];
  onChange?: (items: T[]) => void;
};

export type AsyncSelectProps<T> = Omit<SelectProps<T>, "noSearch"> & {
  isLoading?: boolean;
};

export type SingleSelectProps<T> = Omit<SelectProps<T>, "selectedItems" | "onChange"> & {
  selectedItem: T | null;
  onChange: (item: T | null) => void;
};

export type AsyncSingleSelectProps<T> = Omit<SingleSelectProps<T>, "noSearch"> & {
  isLoading?: boolean;
};

function BaseSelect<T>({
  id,
  readOnlyItems,
  idField,
  labelField,
  isSuggestionField,
  size,
  placeholder,
  iconName,
  showToggleButton,
  inError = false,
  isOutline = false,
  onClearSuggestions,
  onChange,
  onTermChange,
  children,
  singleSelect,
  selectedItems,
  items,
  disabled = false,
  isFilterMode,
  isLoading = false,
  errorMessage = "",
  color = "info",
  keepOrder = true,
  showCreateAction = false,
  showClearButton = true,
  createActionText = "Create new",
  itemsRenderer,
  onClear,
  onCreatClick,
  onRemoveSuggestion,
  inputAppendComponent,
  isCanCreateTag = false,
  ignoreClickOutside = false,
  onCreateTag,
  noSearch = false,
  onClose,
  dropdownZIndex = 2000,
  quickSearchPredicate,
  inputSearchMode,
  eraseSearchTermOnClick,
  top,
  autofocus,
  noResultMessage
}: BaseSelectProps<T>): React.ReactElement {
  const allSelectedItems = React.useMemo<T[]>(() => {
    return mapToNormalizeSelectedItems(selectedItems, singleSelect, idField, readOnlyItems);
  }, [singleSelect, selectedItems, readOnlyItems, idField]);

  const [
    {
      displayedText,
      searchTerm,
      items: filteredItems,
      isDropdownOpen,
      hasFocus,
      inputRef,
      isReadonly,
      showClearButton: showClearButtonState,
    },
    { toggleDropdown, toggleFocus, search, clearSelection, selectOption, createTag },
  ] = useSelect<T>(
    items,
    idField,
    labelField,
    singleSelect,
    allSelectedItems,
    isFilterMode,
    onChange,
    readOnlyItems,
    onCreateTag,
    showClearButton,
    onTermChange,
    onClose,
    quickSearchPredicate,
    eraseSearchTermOnClick
  );

  const rootRef = useOnclickOutside(
    () => {
      toggleDropdown(false);
      toggleFocus(false);
    },
    { rootId: id, disabled: ignoreClickOutside }
  );

  const handleOnClear = (): void => {
    if (onClear) {
      onClear();
    } else {
      clearSelection();
    }
  };

  const handleOnCreateClick = (): void => {
    toggleDropdown?.(false);
    onCreatClick?.(searchTerm)
  }

  return (
    <div className="dropdown position-relative" id={id} ref={rootRef}>
      <SelectInput
        size={size}
        color={color}
        placeholder={placeholder}
        iconName={iconName}
        inError={inError}
        isOutline={isOutline}
        showToggleButton={showToggleButton}
        isDropdownOpen={isDropdownOpen}
        hasFocus={hasFocus}
        displayedText={displayedText}
        searchTerm={searchTerm}
        selectedItemsCount={allSelectedItems.length}
        inputRef={inputRef}
        onToggleDropdown={toggleDropdown}
        onToggleFocus={toggleFocus}
        onClearSelection={handleOnClear}
        onSearchTermChange={search}
        disabled={disabled}
        readonly={isReadonly}
        showClearButton={showClearButtonState}
        inputAppendComponent={inputAppendComponent}
        noSearch={noSearch}
        inputSearchMode={inputSearchMode}
        autofocus={autofocus}
      />
      <SelectOptions<T>
        items={itemsRenderer ? items : filteredItems}
        selectedItems={allSelectedItems}
        readOnlyItems={readOnlyItems}
        idField={idField}
        isSuggestionField={isSuggestionField}
        onClearSuggestions={onClearSuggestions}
        onRemoveSuggestion={onRemoveSuggestion}
        labelField={labelField}
        isDropdownOpen={isDropdownOpen}
        onSelect={selectOption}
        searchTerm={searchTerm}
        isFilterMode={isFilterMode}
        isLoading={isLoading}
        errorMessage={errorMessage}
        inError={inError}
        color={color}
        keepOrder={keepOrder}
        showCreateAction={showCreateAction}
        createActionText={createActionText}
        itemsRenderer={itemsRenderer}
        onCreatClick={handleOnCreateClick}
        isCanCreateTag={isCanCreateTag}
        onCreateTag={createTag}
        dropdownZIndex={dropdownZIndex}
        top={top}
        singleSelect={singleSelect}
        noResultMessage={noResultMessage}
      >
        {children}
      </SelectOptions>
    </div>
  );
}

function Select<T>(props: SelectProps<T>): React.ReactElement {
  const handleOnChange = (item: SelectionType<T>): void => props.onChange?.(mapToAnArrayOfItems(item));
  return <BaseSelect {...props} singleSelect={false} isLoading={false} isFilterMode={true} onChange={handleOnChange} />;
}

function AsyncSelect<T>({ ...props }: AsyncSelectProps<T>): React.ReactElement {
  const handleOnChange = (item: SelectionType<T>): void => props.onChange?.(mapToAnArrayOfItems(item));
  return <BaseSelect {...props} singleSelect={false} isFilterMode={false} onChange={handleOnChange} />;
}

function SingleSelect<T>({ ...props }: SingleSelectProps<T>): React.ReactElement {
  const handleOnChange = (item: SelectionType<T>): void => props.onChange?.(mapToSingleItem(item));
  return (
    <BaseSelect
      {...props}
      singleSelect={true}
      isLoading={false}
      isFilterMode={true}
      selectedItems={props.selectedItem}
      onChange={handleOnChange}
    />
  );
}

function AsyncSingleSelect<T>({ ...props }: AsyncSingleSelectProps<T>): React.ReactElement {
  const handleOnChange = (item: SelectionType<T>): void => props.onChange?.(mapToSingleItem(item));
  return (
    <BaseSelect
      {...props}
      singleSelect={true}
      isFilterMode={false}
      selectedItems={props.selectedItem}
      onChange={handleOnChange}
    />
  );
}

Select.AsyncSelect = AsyncSelect;
Select.SingleSelect = SingleSelect;
Select.AsyncSingleSelect = AsyncSingleSelect;

export default Select;
